import { useState, useEffect } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';

type TabKey = 'mobile' | 'dispatch' | 'commands';

export interface ShortcutsModalProps {
  open: boolean;
  theme?: string;
  onCancel: () => void;
}

export function ShortcutsModal({
  open,
  theme,
  onCancel,
}: ShortcutsModalProps) {
  const [activeTab, setActiveTab] = useState<TabKey>('mobile');

  // Reset to mobile tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab('mobile');
    }
  }, [open]);

  return (
    <BaseFormModal
      open={open}
      title="Shortcuts"
      theme={theme}
      dialogClass="kstc-dialog--large"
      showSave={false}
      cancelText="Close"
      bodyClass="kstc-body--scroll"
      onCancel={onCancel}
      subheader={
        <div className="kstc-tabs">
          <button
            type="button"
            className={`kstc-tab${activeTab === 'mobile' ? ' kstc-tab--active' : ''}`}
            onClick={() => setActiveTab('mobile')}
          >
            Mobile Shortcuts
          </button>
          <button
            type="button"
            className={`kstc-tab${activeTab === 'dispatch' ? ' kstc-tab--active' : ''}`}
            onClick={() => setActiveTab('dispatch')}
          >
            Dispatch Shortcuts
          </button>
          <button
            type="button"
            className={`kstc-tab${activeTab === 'commands' ? ' kstc-tab--active' : ''}`}
            onClick={() => setActiveTab('commands')}
          >
            Dispatch Commands
          </button>
        </div>
      }
    >
      {/* Mobile Shortcuts Tab */}
      <div style={{ display: activeTab === 'mobile' ? undefined : 'none' }}>
        <h3 className="kstc-section-title">General Shortcuts</h3>
        <table className="kstc-shortcut-table">
          <thead>
            <tr>
              <th>To Do</th>
              <th>Shortcut</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Open <strong>SHORTCUTS</strong></td><td>Alt + A</td></tr>
            <tr><td>Go to the <strong>HOME</strong> screen</td><td>F1</td></tr>
            <tr><td><strong>SELF INITIATE</strong> or go to <strong>MY CALL</strong></td><td>F2</td></tr>
            <tr><td>Go to <strong>IN PROGRESS</strong> screen</td><td>F3</td></tr>
            <tr><td>Go to <strong>STATUS</strong> screen</td><td>F4</td></tr>
            <tr><td>Go to the <strong>LOG</strong> screen</td><td>F5</td></tr>
            <tr><td>Go to the <strong>MAP</strong> screen</td><td>F6</td></tr>
            <tr><td>Go to the <strong>NOTES</strong> screen</td><td>F7</td></tr>
            <tr><td>Go to the <strong>SEARCH</strong> screen</td><td>F8</td></tr>
            <tr><td>Go to the <strong>REPORTS</strong> screen</td><td>F9</td></tr>
            <tr><td>Go to the <strong>MESSAGING</strong> screen</td><td>F10</td></tr>
            <tr><td>View your <strong>USER PROFILE</strong></td><td>Alt + U</td></tr>
            <tr><td>View the dropdown <strong>MENU</strong></td><td>Alt + M</td></tr>
            <tr><td>Change to <strong>DAY THEME</strong></td><td>Alt + 1</td></tr>
            <tr><td>Change to <strong>NIGHT BLUE THEME</strong></td><td>Alt + 2</td></tr>
            <tr><td>Change to <strong>NIGHT GREY THEME</strong></td><td>Alt + 3</td></tr>
          </tbody>
        </table>

        <h3 className="kstc-section-title">Calls For Service Shortcuts</h3>
        <table className="kstc-shortcut-table">
          <thead>
            <tr>
              <th>To Do</th>
              <th>Shortcut</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Enable edit mode for <strong>ASSIGNED</strong> call</td><td>Alt + E</td></tr>
            <tr><td><strong>SELF ASSIGN</strong> to a call</td><td>Alt + F1</td></tr>
            <tr><td>Update my <strong>ENROUTE</strong> time on assigned call</td><td>Alt + F2</td></tr>
            <tr><td>Update my <strong>ONSCENE</strong> time on assigned call</td><td>Alt + F3</td></tr>
            <tr><td>Update my <strong>CLEARED</strong> time on assigned call</td><td>Alt + F5</td></tr>
          </tbody>
        </table>
      </div>

      {/* Dispatch Shortcuts Tab */}
      <div style={{ display: activeTab === 'dispatch' ? undefined : 'none' }}>
        <h3 className="kstc-section-title">General Shortcuts</h3>
        <table className="kstc-shortcut-table">
          <thead>
            <tr>
              <th>To Do</th>
              <th>Shortcut</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Open <strong>SHORTCUTS</strong></td><td>Alt + A</td></tr>
            <tr><td>Go to the <strong>HOME</strong> screen</td><td>F1</td></tr>
            <tr><td>Go to <strong>IN PROGRESS</strong> screen</td><td>F2</td></tr>
            <tr><td>Go to <strong>STATUS</strong> screen</td><td>F3</td></tr>
            <tr><td>Create new <strong>CALL</strong></td><td>F4</td></tr>
            <tr><td>Send/Update <strong>CALL</strong></td><td>Alt + S</td></tr>
            <tr><td>Go to the <strong>LOG</strong> screen</td><td>F5</td></tr>
            <tr><td>Go to the <strong>MAP</strong> screen</td><td>F6</td></tr>
            <tr><td>Go to the <strong>NOTE</strong> screen</td><td>F7</td></tr>
            <tr><td>Go to the <strong>SEARCH</strong> screen</td><td>F8</td></tr>
            <tr><td>Go to the <strong>MESSAGING</strong> screen</td><td>F10</td></tr>
            <tr><td>View your <strong>USER PROFILE</strong></td><td>Alt + U</td></tr>
            <tr><td>View the dropdown <strong>MENU</strong></td><td>Alt + M</td></tr>
            <tr><td>Change to <strong>DAY THEME</strong></td><td>Alt + 1</td></tr>
            <tr><td>Change to <strong>NIGHT BLUE THEME</strong></td><td>Alt + 2</td></tr>
            <tr><td>Change to <strong>NIGHT GREY THEME</strong></td><td>Alt + 3</td></tr>
          </tbody>
        </table>
      </div>

      {/* Dispatch Commands Tab */}
      <div style={{ display: activeTab === 'commands' ? undefined : 'none' }}>
        <h3 className="kstc-section-title">COMMANDS</h3>
        <p className="kstc-message">
          To show the command line, please type &quot;/&quot; in the DISPATCH module.
        </p>
        <table className="kstc-shortcut-table">
          <thead>
            <tr>
              <th>To Do</th>
              <th>Command</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Update officer <strong>SECONDARY LOCATION</strong> screen</td>
              <td>sl;&lt;call sign&gt;;&lt;secondary location&gt;</td>
            </tr>
            <tr className="kstc-shortcut-example">
              <td>
                Example - updates officer with call sign T-3 to have secondary
                location &quot;EXIT RAMP | 14&quot; on DISPATCH In Progress screen.
              </td>
              <td>sl;T-3;EXIT RAMP | 14</td>
            </tr>
          </tbody>
        </table>
      </div>
    </BaseFormModal>
  );
}

(window as any).ShortcutsModalReact = ShortcutsModal;
